import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import FilterPanel from "../../../component/FilterPanel/FilterPanel";
import request from "../../../api/request";
import { BackendAPI } from "../../../const/api";
import "./ListPage.css";
import { useToast } from "../../../component/ToastBar";
import { AuthContext } from "../../../auth/authContext";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { ArrowDownward, ArrowUpward, Delete, Add } from "@mui/icons-material";

type ProductAlbumItem = {
  id: string;
  code: string;
  name: string;
  model: string;
  intro: string;
  mainImageId?: string | null;
  hasMainThumb?: boolean;
};

type ProductImage = {
  id: string;
  materialCode: string;
  storageType: "local" | "oss" | string;
  path: string;
  thumbPath?: string | null;
  isMain: boolean;
};

type ExportPoolItem = {
  code: string;
  name: string;
  model: string;
};

const baseUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";

const ProductAlbumList = () => {
  const toast = useToast();
  const authContext = useContext(AuthContext);

  const [items, setItems] = useState<ProductAlbumItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<ProductAlbumItem | null>(null);
  const [intro, setIntro] = useState("");
  const [images, setImages] = useState<ProductImage[]>([]);

  // export
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormats, setExportFormats] = useState<any[]>([]);
  const [exportFormatId, setExportFormatId] = useState<string>("");
  const [exportAll, setExportAll] = useState(true); // true: 当前搜索全部；false: 手动多选
  const [exportSearchQ, setExportSearchQ] = useState("");
  const [exportSearchPage, setExportSearchPage] = useState(0);
  const [exportSearchRowsPerPage, setExportSearchRowsPerPage] = useState(20);
  const [exportSearchTotal, setExportSearchTotal] = useState(0);
  const [exportSearchItems, setExportSearchItems] = useState<ProductAlbumItem[]>([]);
  const [exportSearchSelectedCodes, setExportSearchSelectedCodes] = useState<Record<string, boolean>>({});
  const [exportPool, setExportPool] = useState<ExportPoolItem[]>([]);

  const imageUrl = useCallback(
    (imageId: string, thumb: boolean) => {
      const suffix = thumb ? "/thumb" : "";
      const token = localStorage.getItem("token") || "";
      return `${baseUrl}/api/product_album/image/${imageId}${suffix}?token=${encodeURIComponent(token)}`;
    },
    [],
  );

  const fetchList = useCallback(async () => {
    const params = { page: page + 1, size: rowsPerPage, ...filters };
    const res = await request.get(BackendAPI.productAlbumList, params);
    if (!res?.success) return;
    setItems(res.data.items || []);
    setTotalCount(res.data.total || 0);
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchList().then();
  }, [fetchList]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const openDialog = async (item: ProductAlbumItem) => {
    setCurrent(item);
    setIntro(item.intro || "");
    setImages([]);
    setOpen(true);
    const res = await request.get(BackendAPI.productAlbumListImages(item.code));
    if (res?.success) {
      setImages(res.data.images || []);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setCurrent(null);
    setImages([]);
    setIntro("");
  };

  const saveIntro = async () => {
    if (!current) return;
    const res = await request.put(BackendAPI.productAlbumUpdateInfo(current.code), { intro });
    if (res?.success) {
      toast("保存成功");
      await fetchList();
      setCurrent({ ...current, intro });
    }
  };

  const refreshImages = async () => {
    if (!current) return;
    const res = await request.get(BackendAPI.productAlbumListImages(current.code));
    if (res?.success) {
      setImages(res.data.images || []);
      await fetchList();
    }
  };

  const handleUploadImages = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !current) return;

    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append("files", f));

    const res = await request.post(BackendAPI.productAlbumUploadImages(current.code), null, formData);
    if (res?.success) {
      toast("上传成功");
      await refreshImages();
    } else {
      toast(res?.message || "上传失败", "error");
    }
    event.target.value = "";
  };

  const handleDeleteImage = async (imageId: string) => {
    const res = await request.del(BackendAPI.productAlbumDeleteImage(imageId));
    if (res?.success) {
      toast("删除成功");
      await refreshImages();
    }
  };

  const handleSetMain = async (imageId: string) => {
    const res = await request.post(BackendAPI.productAlbumSetMain(imageId), {});
    if (res?.success) {
      toast("已设为主图");
      await refreshImages();
    }
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const res = await request.post(BackendAPI.productAlbumImport, null, formData);
    if (res?.success) {
      toast(`导入完成：介绍${res.data.updatedIntro}条，主图${res.data.updatedMain}条`);
      await fetchList();
    }
    event.target.value = "";
  };

  const openExportDialog = async () => {
    const res = await request.get(BackendAPI.productAlbumExportFormats);
    if (res?.success) {
      setExportFormats(res.data.formats || []);
      if (!exportFormatId && res.data.formats?.length) {
        setExportFormatId(res.data.formats[0].id);
      }
      setExportAll(true);
      setExportSearchQ("");
      setExportSearchPage(0);
      setExportSearchRowsPerPage(20);
      setExportSearchTotal(0);
      setExportSearchItems([]);
      setExportSearchSelectedCodes({});
      setExportPool([]);
      setExportOpen(true);
    }
  };

  const closeExportDialog = () => {
    setExportOpen(false);
  };

  const fetchExportSearch = async (page0?: number, size?: number) => {
    const p = page0 ?? exportSearchPage;
    const s = size ?? exportSearchRowsPerPage;
    const q = exportSearchQ.trim();
    const res = await request.get(BackendAPI.productAlbumList, {
      page: p + 1,
      size: s,
      q,
    });
    if (!res?.success) return;
    setExportSearchItems(res.data.items || []);
    setExportSearchTotal(res.data.total || 0);
    setExportSearchSelectedCodes({});
  };

  const addToPool = (it: ProductAlbumItem) => {
    setExportPool((prev) => {
      if (prev.some((p) => p.code === it.code)) return prev;
      return [...prev, { code: it.code, name: it.name, model: it.model }];
    });
  };

  const addSelectedToPool = () => {
    const codes = Object.keys(exportSearchSelectedCodes).filter((k) => exportSearchSelectedCodes[k]);
    if (codes.length === 0) {
      toast("请先勾选要加入的物料", "error");
      return;
    }
    const codeSet = new Set(codes);
    setExportPool((prev) => {
      const existed = new Set(prev.map((p) => p.code));
      const toAdd: ExportPoolItem[] = [];
      exportSearchItems.forEach((it) => {
        if (codeSet.has(it.code) && !existed.has(it.code)) {
          toAdd.push({ code: it.code, name: it.name, model: it.model });
        }
      });
      return [...prev, ...toAdd];
    });
  };

  const removeFromPool = (code: string) => {
    setExportPool((prev) => prev.filter((p) => p.code !== code));
  };

  const movePoolItem = (idx: number, dir: -1 | 1) => {
    setExportPool((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[j];
      next[j] = tmp;
      return next;
    });
  };

  const doExport = async () => {
    if (!exportFormatId) {
      toast("请选择导出格式", "error");
      return;
    }
    const q = (filters as any)?.q || "";
    const materialCodes = exportAll ? null : exportPool.map((p) => p.code);
    if (!exportAll && exportPool.length === 0) {
      toast("待导出池为空，请先添加物料", "error");
      return;
    }
    const res = await request.post(BackendAPI.productAlbumExport, {
      formatId: exportFormatId,
      q: q || "",
      materialCodes: materialCodes,
    });
    if (res instanceof Blob) {
      const url = window.URL.createObjectURL(res);
      window.open(url, "_blank");
      toast("导出成功");
      setExportOpen(false);
    }
  };

  const canAccess =
    authContext?.isAdmin() ||
    authContext?.isBA() ||
    authContext?.isSalesScopeRole() ||
    authContext?.isFinance() ||
    authContext?.isWarehouse();

  if (!canAccess) {
    return <Paper style={{ padding: "16px" }}>无权限</Paper>;
  }

  return (
    <Paper>
      <FilterPanel
        filterOptions={[
          { key: "q", label: "关键字（编码/名称/型号）", type: "text" },
        ]}
        onFilterChange={handleFilterChange}
      />

      <div style={{ padding: "16px" }}>
        <Button variant="contained" onClick={openExportDialog} style={{ marginRight: 8 }}>
          导出图册
        </Button>
        <input
          accept=".xlsx"
          style={{ display: "none" }}
          id="product-album-import"
          type="file"
          onChange={handleImportExcel}
        />
        <label htmlFor="product-album-import">
          <Button variant="outlined" component="span">
            Excel批量导入（介绍/主图）
          </Button>
        </label>
      </div>

      <Table className="list-table">
        <TableHead>
          <TableRow>
            <TableCell className="column-width-small">物料ID</TableCell>
            <TableCell className="column-width-small">物料编码</TableCell>
            <TableCell className="column-width-small">物料名</TableCell>
            <TableCell className="column-width-medium">型号规格</TableCell>
            <TableCell className="column-width-large">产品介绍</TableCell>
            <TableCell className="column-width-small">图片</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.code}>
              <TableCell>{it.id}</TableCell>
              <TableCell>{it.code}</TableCell>
              <TableCell>{it.name}</TableCell>
              <TableCell>{it.model}</TableCell>
              <TableCell style={{ maxWidth: 520, whiteSpace: "pre-wrap" }}>
                {it.intro || "-"}
              </TableCell>
              <TableCell>
                {it.mainImageId ? (
                  <img
                    src={imageUrl(it.mainImageId, !!it.hasMainThumb)}
                    style={{ width: 56, height: 56, objectFit: "cover", cursor: "pointer" }}
                    loading="lazy"
                    onClick={() => openDialog(it)}
                    alt="主图"
                  />
                ) : (
                  <Button size="small" variant="outlined" onClick={() => openDialog(it)}>
                    查看/编辑
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
      />

      {open && current && (
        <Dialog open={open} onClose={closeDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            产品图册 - {current.code} / {current.name}
          </DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", gap: "16px", marginBottom: "12px", alignItems: "flex-start" }}>
              <TextField
                label="产品介绍"
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
              <div style={{ minWidth: 220 }}>
                <Button variant="contained" onClick={saveIntro} style={{ width: "100%" }}>
                  保存介绍
                </Button>
                <div style={{ height: 8 }} />
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="product-album-upload"
                  type="file"
                  multiple
                  onChange={handleUploadImages}
                />
                <label htmlFor="product-album-upload" style={{ width: "100%", display: "block" }}>
                  <Button variant="outlined" component="span" style={{ width: "100%" }}>
                    上传图片
                  </Button>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              {images.map((img) => {
                const isOssHttp =
                  (img.storageType || "").toLowerCase() === "oss" &&
                  (img.path || "").toLowerCase().startsWith("http");
                const src =
                  isOssHttp
                    ? img.path
                    : (img.storageType || "").toLowerCase() === "local"
                      ? imageUrl(img.id, true)
                      : "";
                return (
                  <div
                    key={img.id}
                    style={{
                      width: 160,
                      border: img.isMain ? "2px solid #1976d2" : "1px solid #ddd",
                      padding: 8,
                    }}
                  >
                    {src ? (
                      <img
                        src={src}
                        style={{ width: "100%", height: 120, objectFit: "cover" }}
                        loading="lazy"
                        alt={img.isMain ? "主图" : "图片"}
                      />
                    ) : (
                      <div style={{ height: 120, display: "flex", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "#666" }}>无法预览</span>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={img.isMain}
                        onClick={() => handleSetMain(img.id)}
                      >
                        设为主图
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteImage(img.id)}
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                );
              })}
              {images.length === 0 && <span style={{ color: "#666" }}>暂无图片</span>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>关闭</Button>
          </DialogActions>
        </Dialog>
      )}

      {exportOpen && (
        <Dialog open={exportOpen} onClose={closeExportDialog} maxWidth="md" fullWidth>
          <DialogTitle>导出产品图册</DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <span style={{ minWidth: 72 }}>格式</span>
              <Select
                size="small"
                value={exportFormatId}
                onChange={(e) => setExportFormatId(String(e.target.value))}
                style={{ minWidth: 240 }}
              >
                {exportFormats.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <FormControlLabel
                control={<Checkbox checked={exportAll} onChange={(e) => setExportAll(e.target.checked)} />}
                label="导出当前搜索结果全部（推荐）"
              />
              <span style={{ color: "#666", fontSize: 12 }}>
                当前搜索关键字：{((filters as any)?.q || "").trim() || "（空=全部）"}
              </span>
            </div>

            {!exportAll && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <TextField
                    size="small"
                    label="搜索物料（编码/名称/型号）"
                    value={exportSearchQ}
                    onChange={(e) => setExportSearchQ(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      setExportSearchPage(0);
                      fetchExportSearch(0, exportSearchRowsPerPage);
                    }}
                  >
                    搜索
                  </Button>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 420 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                      <Button size="small" variant="outlined" onClick={addSelectedToPool}>
                        批量加入待导出池
                      </Button>
                      <span style={{ color: "#666", fontSize: 12 }}>
                        当前结果已勾选 {Object.values(exportSearchSelectedCodes).filter(Boolean).length} 个
                      </span>
                    </div>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>物料编码</TableCell>
                          <TableCell>物料名</TableCell>
                          <TableCell>型号</TableCell>
                          <TableCell align="right">操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exportSearchItems.map((it) => (
                          <TableRow key={it.code}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={!!exportSearchSelectedCodes[it.code]}
                                onChange={() =>
                                  setExportSearchSelectedCodes((prev) => ({
                                    ...prev,
                                    [it.code]: !prev[it.code],
                                  }))
                                }
                              />
                            </TableCell>
                            <TableCell>{it.code}</TableCell>
                            <TableCell>{it.name}</TableCell>
                            <TableCell>{it.model}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={() => addToPool(it)}
                              >
                                加入
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {exportSearchItems.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} style={{ color: "#666" }}>
                              请输入关键字搜索，然后把结果加入“待导出池”
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={exportSearchTotal}
                      page={exportSearchPage}
                      rowsPerPage={exportSearchRowsPerPage}
                      onPageChange={(_, newPage) => {
                        setExportSearchPage(newPage);
                        fetchExportSearch(newPage, exportSearchRowsPerPage);
                      }}
                      onRowsPerPageChange={(event) => {
                        const s = parseInt(event.target.value, 10);
                        setExportSearchRowsPerPage(s);
                        setExportSearchPage(0);
                        fetchExportSearch(0, s);
                      }}
                    />
                  </div>

                  <Divider orientation="vertical" flexItem />

                  <div style={{ width: 320 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>待导出池</span>
                      <span style={{ color: "#666", fontSize: 12 }}>
                        {exportPool.length} 个（支持排序）
                      </span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setExportPool([])}
                        style={{ marginLeft: "auto" }}
                      >
                        清空
                      </Button>
                    </div>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: 44 }}>顺序</TableCell>
                          <TableCell>物料</TableCell>
                          <TableCell align="right" style={{ width: 120 }}>
                            操作
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exportPool.map((p, idx) => (
                          <TableRow key={p.code}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>
                              <div style={{ fontWeight: 600 }}>{p.code}</div>
                              <div style={{ color: "#666", fontSize: 12 }}>
                                {p.name} / {p.model}
                              </div>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => movePoolItem(idx, -1)}
                                disabled={idx === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => movePoolItem(idx, 1)}
                                disabled={idx === exportPool.length - 1}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => removeFromPool(p.code)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {exportPool.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} style={{ color: "#666" }}>
                              暂无待导出物料
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <div style={{ color: "#666", marginTop: 6, fontSize: 12 }}>
                      可多轮搜索，把结果加入池子后再导出；导出顺序=池子顺序
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeExportDialog}>取消</Button>
            <Button onClick={doExport} variant="contained">
              导出
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default ProductAlbumList;


